import React, { useEffect, useState } from 'react';
import { Tour } from 'antd';
import { useTour } from '../../../hooks/useTour';
import { getComprehensiveTourSteps } from '../../../constants/comprehensiveTourSteps';
import { useTranslation } from 'react-i18next';
import '../../../style/scss/tour.scss';

const AppTour: React.FC = () => {
  const { t } = useTranslation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const {
    isOpen,
    currentStep,
    stepIndex,
    isNavigating,
    handleNextTourStep,
    handlePrevTourStep,
    handleCloseTour, // Bu fonksiyon turu kapatır ve kaydeder
    handleCompleteTour,
    getStepDisplayName,
  } = useTour();
  
  useEffect(() => {
    if (isOpen && !isNavigating) {
      setCurrentStepIndex(0);
    }
  }, [isOpen, currentStep, isNavigating]);

  const handleClose = () => {
    // Modal yok, doğrudan kapat ve kaydet.
    setCurrentStepIndex(0);
    handleCloseTour(); 
  };
  
  // Mevcut step'in tour adımlarını al
  const currentStepTours = getComprehensiveTourSteps(currentStep);

  const handleNext = () => {
    if (currentStepIndex < currentStepTours.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      if (currentStep === 'completed') {
        handleCompleteTour();
      } else {
        handleNextTourStep();
      }
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      if (stepIndex > 0) {
        handlePrevTourStep();
        setTimeout(() => {
          const prevStepTours = getComprehensiveTourSteps(currentStep);
          setCurrentStepIndex(prevStepTours.length - 1);
        }, 600);
      }
    }
  };

  const handleFinish = () => {
    setCurrentStepIndex(0);
    if (currentStep === 'completed') {
      handleCompleteTour();
    } else {
      handleNextTourStep();
    }
  };

  // Tour steps'lerini custom butonlarla güncelle
  const steps = currentStepTours.map((step, index) => {
    const isLastTourOfStep = index === currentStepTours.length - 1;
    const isFirstTourOfStep = index === 0;
    const isLastStep = currentStep === 'completed';

    return {
      ...step,
      target: step.target === 'body' ? null : () => {
        const element = document.querySelector(step.target);
        return element as HTMLElement;
      },
      nextButtonProps: isLastTourOfStep ? {
        children: isLastStep ? t('tour.finish') : 'Sonraki Bölüm →',
        onClick: handleFinish,
        className: 'ant-btn-primary tour-next-btn',
      } : {
        children: t('tour.next'),
        onClick: handleNext,
        className: 'ant-btn-primary tour-next-btn',
      },
      prevButtonProps: (!isFirstTourOfStep || stepIndex > 0) ? {
        children: isFirstTourOfStep && stepIndex > 0 ? '← Önceki Bölüm' : t('tour.previous'),
        onClick: handlePrev,
        className: 'tour-prev-btn',
      } : undefined,
    };
  });

  if (!isOpen || currentStepTours.length === 0) {
    return null;
  }
  
  // Dino seçimi - her adımda farklı dino
  const getDinoImage = () => {
    const totalProgress = stepIndex;
    const isFemale = totalProgress % 2 === 1;
    
    return {
      src: isFemale ? "/assets/img/dinow.png" : "/assets/img/dino.png",
      alt: "Dinox",
      emoji: isFemale ? "🦕👩" : "🦕👨"
    };
  };

  // Custom Progress Component
  const renderCustomProgress = () => {
    const dinoInfo = getDinoImage();
    
    return (
      <div className="tour-custom-progress">
        <div className="tour-dino-container">
          <div 
            className={`tour-dino-image ${dinoInfo.src.includes('dinow') ? 'dino-female' : 'dino-male'}`}
            title={dinoInfo.alt}
          >
            <img 
              src={dinoInfo.src}
              alt={dinoInfo.alt}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = `<div style="font-size: 24px; color: white;">${dinoInfo.emoji}</div>`;
              }}
            />
          </div>
        </div>
        
        <div className="tour-progress-info">
          <div className="tour-step-title">
            <span className="tour-step-emoji">🎯</span>
            {getStepDisplayName(currentStep)}
          </div>
          
          <div className="tour-step-counter-new">
            Bölüm {stepIndex + 1}/22 • Adım {currentStepIndex + 1}/{currentStepTours.length}
          </div>
          
          <div className="tour-progress-bar-container">
            <div 
              className="tour-progress-bar-fill"
              style={{
                width: `${((stepIndex + 1) / 22) * 100}%`
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Tour
        open={isOpen}
        onClose={handleClose} // 'X' ikonuna basıldığında doğrudan handleClose'u çağır
        steps={steps.map(step => ({
          ...step,
          description: (
            <div>
              {step.description}
              {renderCustomProgress()}
            </div>
          )
        }))}
        current={currentStepIndex}
        type="primary"
        arrow={{ pointAtCenter: true }}
        placement="bottom"
        mask={{
          style: {
            boxShadow: 'inset 0 0 15px #333',
          },
          color: 'rgba(0, 0, 0, 0.6)',
        }}
        zIndex={1002}
        gap={{ offset: 6, radius: 6 }}
        scrollIntoViewOptions={{ behavior: 'smooth', block: 'center' }}
      />
    </>
  );
};

export default AppTour;